/**
 * エピソードの絞り込み。DOM に触らない純粋な関数だけを置く（Node からテストできるように）。
 * 「どの値が選べるか」は facetsOf() がデータセットから作り、UI はそれを描くだけにする。
 */

/** 「タスク未設定」を表すキー。空文字なので実タスク名とぶつからない */
export const NO_TASK = '';

export function emptyFilter(){
  return { q: '', tasks: new Set(), secMin: null, secMax: null,
           epMin: null, epMax: null, video: 'all', file: 'all', cam: 'all' };
}

/** 既定値のままか（= 全件表示か） */
export function isActive(f){
  return !!f.q.trim() || f.tasks.size > 0 || f.secMin != null || f.secMax != null ||
         f.epMin != null || f.epMax != null || f.video !== 'all' || f.file !== 'all' || f.cam !== 'all';
}

/**
 * 選択肢を作る。
 * @param ds      Dataset
 * @param missing Map<episodeIndex, string[]>  動画が見つからなかったカメラ名
 */
export function facetsOf(ds, missing = new Map()){
  const eps = ds.episodes;
  const counts = new Map();
  for(const e of eps) for(const t of e.tasks) counts.set(t, (counts.get(t) || 0) + 1);

  // taskList はカタログ順。エピソードが 0 件のタスクもここに含まれる
  const tasks = ds.taskList.map(t => ({ task: t.task, n: counts.get(t.task) ?? t.n ?? 0 }));
  const untasked = eps.filter(e => e.tasks.length === 0).length;
  if(untasked) tasks.push({ task: NO_TASK, n: untasked });

  const files = new Map();
  for(const e of eps) files.set(e.dataPath, (files.get(e.dataPath) || 0) + 1);

  const secs = eps.map(e => e.sec);
  const idx = eps.map(e => e.i);
  const incomplete = eps.filter(e => (missing.get(e.i)?.length ?? 0) > 0).length;

  return {
    tasks,
    files: [...files].sort((a, b) => a[0] < b[0] ? -1 : 1).map(([path, n]) => ({ path, n })),
    cams: ds.cams.map(c => ({ cam: c, n: eps.filter(e => !!e.cams[c]).length })),
    sec: secs.length ? [Math.min(...secs), Math.max(...secs)] : [0, 0],
    ep: idx.length ? [Math.min(...idx), Math.max(...idx)] : [0, 0],
    incomplete,
    total: eps.length,
  };
}

/** 空白区切りの語をすべて含むこと（タスク名・エピソード番号・データファイル名が対象） */
function matchesQuery(ep, q){
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  if(!terms.length) return true;
  const hay = `#${ep.i} ${ep.tasks.join(' ')} ${ep.dataPath}`.toLowerCase();
  return terms.every(t => hay.includes(t));
}

export function matchesEpisode(ep, f, ctx = {}){
  const missing = ctx.missing?.get(ep.i) ?? [];

  if(f.tasks.size){
    const hit = ep.tasks.length ? ep.tasks.some(t => f.tasks.has(t)) : f.tasks.has(NO_TASK);
    if(!hit) return false;
  }
  if(f.secMin != null && ep.sec < f.secMin) return false;
  if(f.secMax != null && ep.sec > f.secMax) return false;
  if(f.epMin != null && ep.i < f.epMin) return false;
  if(f.epMax != null && ep.i > f.epMax) return false;
  if(f.file !== 'all' && ep.dataPath !== f.file) return false;
  if(f.cam !== 'all' && !ep.cams[f.cam]) return false;
  if(f.video === 'complete' && missing.length) return false;
  if(f.video === 'missing' && !missing.length) return false;
  if(!matchesQuery(ep, f.q.trim())) return false;
  return true;
}

/** 絞り込み + 並び替えを行い、エピソード番号の配列を返す */
export function orderOf(ds, f, sort, ctx = {}){
  const eps = ds.episodes.filter(e => matchesEpisode(e, f, ctx));
  const rank = t => ds.taskIndex.get(t) ?? Number.MAX_SAFE_INTEGER;
  const cmp = {
    'i': (a, b) => a.i - b.i,
    'len-': (a, b) => b.sec - a.sec || a.i - b.i,
    'len+': (a, b) => a.sec - b.sec || a.i - b.i,
    'task': (a, b) => rank(a.tasks[0] ?? '') - rank(b.tasks[0] ?? '') || a.i - b.i,
  }[sort] || ((a, b) => a.i - b.i);
  return eps.sort(cmp).map(e => e.i);
}

const n1 = v => Number.isInteger(v) ? String(v) : v.toFixed(1);

/** 片側だけ指定されることが多いので、「8 秒以上」「#3 以下」のように読める形にする */
function range(lo, hi, unit){
  if(lo != null && hi != null) return `${n1(lo)}–${n1(hi)}${unit}`;
  if(lo != null) return `${n1(lo)}${unit}以上`;
  return `${n1(hi)}${unit}以下`;
}

/** 折りたたんだときに「今なにで絞っているか」を出すための短い説明 */
export function describeFilter(f, facets){
  const out = [];
  if(f.q.trim()) out.push(`「${f.q.trim()}」`);
  if(f.tasks.size) out.push(`タスク ${f.tasks.size}/${facets.tasks.length} 種`);
  if(f.secMin != null || f.secMax != null) out.push(`長さ ${range(f.secMin, f.secMax, ' 秒')}`);
  if(f.epMin != null || f.epMax != null) out.push(range(f.epMin, f.epMax, '').replace(/^/, '#'));
  if(f.cam !== 'all') out.push(`カメラ ${f.cam}`);
  if(f.video === 'complete') out.push('動画が揃っているもの');
  if(f.video === 'missing') out.push('動画が欠けているもの');
  if(f.file !== 'all') out.push(f.file.split('/').pop());
  return out.join(' · ');
}
